import React, { useState, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import './Standings.css'
import StatsAndStandingsHome from '../../shared/components/Navigation/StatsAndStandingsHome'
import StatsAndStandingsHomeNavigation from '../../shared/components/Navigation/StatsAndStandingsHomeNavigation'
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner'
import ErrorModal from '../../shared/components/UIElements/ErrorModal'
import standingsLogo from '../../images/MurrysvilleSportzone.png'

const Standings = () => {
	const [error, setError] = useState()
	const [isLoading, setIsLoading] = useState(false)
	const [standings, setStandings] = useState([])
	const [leagueName, setLeagueName] = useState('')
	const [divisionName, setDivisionName] = useState('')
	const [session, setSession] = useState('')
	const [year, setYear] = useState('')

	const { leagueId } = useParams()

	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)

			try {
				const res = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/league/leagues/${leagueId}/standings`,
				)

				const data = await res.json()

				if (!res.ok) {
					throw new Error(data.message || 'Failed to load standings')
				}

				const incoming =
					data.standings ?? data.masterMasterArray ?? data.listOfTeams ?? []

				setStandings(Array.isArray(incoming) ? incoming : [])

				setLeagueName(data.leagueName ?? '')
				setSession(data.session ?? '')
				setYear(data.year ?? '')

				const firstTeam =
					data.listOfTeams?.[0] ??
					data.standings?.[0] ??
					data.masterMasterArray?.[0] ??
					null

				setDivisionName(firstTeam?.divisionName ?? '')
			} catch (err) {
				setError(err.message)
			} finally {
				setIsLoading(false)
			}
		}

		sendRequest()
	}, [leagueId])

	const errorHandler = () => {
		setError(null)
	}

	const displayStandings = useMemo(() => {
		const numberOfTeams = standings.length

		if (!numberOfTeams) {
			return []
		}

		return standings.slice(-numberOfTeams)
	}, [standings])

	const getGamesPlayed = (team) => {
		return (
			Number(team.wins || 0) +
			Number(team.losses || 0) +
			Number(team.ties || 0) +
			Number(team.shootoutLosses || 0) +
			Number(team.overtimeLosses || 0)
		)
	}

	const getPoints = (team) => {
		return (
			team.points ??
			Number(team.wins || 0) * 2 +
				Number(team.overtimeLosses || 0) +
				Number(team.shootoutLosses || 0) +
				Number(team.ties || 0)
		)
	}

	const getWinningPercentage = (team) => {
		const gamesPlayed = getGamesPlayed(team)

		if (gamesPlayed === 0) {
			return ''
		}

		const percentage =
			(Number(team.wins || 0) + Number(team.ties || 0) * 0.5) / gamesPlayed

		return percentage.toFixed(3)
	}

	return (
		<div style={{ overflowX: 'hidden' }}>
			<React.Fragment>
				<StatsAndStandingsHome />

				<StatsAndStandingsHomeNavigation leagueId={leagueId} />

				<ErrorModal error={error} onClear={errorHandler} />

				{isLoading && (
					<div className='center'>
						<LoadingSpinner />
					</div>
				)}

				{!isLoading && displayStandings.length > 0 && (
					<div className='Roster standingsRoster'>
						<div className='standingsMobileLogoWrapper'>
							<img
								src={standingsLogo}
								alt='League logo'
								className='standingsMobileLogo'
							/>
						</div>

						<h1 className='standingsMainTitle'>League Standings</h1>

						<div className='standingsMobileAccent'></div>

						<h1 className='standingsLeagueTitle'>
							{leagueName}
							{divisionName && ` - ${divisionName} Division`}
						</h1>

						<h2 className='standingsSeason'>
							{session} {year}
						</h2>

						<p className='clickOn standingsInstructions'>
							(Click on a team to go to their roster and schedule page)
						</p>

						<div className='standingsTableWrapper'>
							<table id='roster' cellSpacing='0' cellPadding='0'>
								<thead>
									<tr>
										<th>Team</th>
										<th>GP</th>
										<th>W</th>
										<th>L</th>
										<th>T</th>
										<th>OTL</th>
										<th>SOL</th>
										<th>GF</th>
										<th>GA</th>
										<th>GD</th>
										<th>Pts</th>
										<th>%</th>
									</tr>
								</thead>

								<tbody>
									{displayStandings.map((team, idx) => {
										const goalsFor = Number(team.goalsFor || 0)

										const goalsAgainst = Number(team.goalsAgainst || 0)

										return (
											<tr key={team._id || idx}>
												<td className='tableDataTeamName'>
													<Link to={`${session}/${year}/${team.teamName}`}>
														{team.teamName}
													</Link>
												</td>

												<td>{getGamesPlayed(team)}</td>

												<td>{Number(team.wins || 0)}</td>

												<td>{Number(team.losses || 0)}</td>

												<td>{Number(team.ties || 0)}</td>

												<td>{Number(team.overtimeLosses || 0)}</td>

												<td>{Number(team.shootoutLosses || 0)}</td>

												<td>{goalsFor}</td>

												<td>{goalsAgainst}</td>

												<td>{goalsFor - goalsAgainst}</td>

												<td>{getPoints(team)}</td>

												<td>{getWinningPercentage(team)}</td>
											</tr>
										)
									})}
								</tbody>
							</table>
						</div>

						<div className='standingsLegend'>
							<span>GP = Games Played</span>
							<span>W = Wins</span>
							<span>L = Losses</span>
							<span>T = Ties</span>
							<span>OTL = Overtime Losses</span>
							<span>SOL = Shootout Losses</span>
							<span>GF = Goals For</span>
							<span>GA = Goals Against</span>
							<span>GD = Goal Differential</span>
							<span>Points = Standings Points</span>
							<span>% = Winning Percentage</span>
						</div>
					</div>
				)}

				{!isLoading && displayStandings.length === 0 && (
					<div className='Standings'>
						<h1>
							{leagueName} {divisionName && divisionName} - League Standings
						</h1>

						<h2>
							{session} {year}
						</h2>

						<table id='players' cellSpacing='0' cellPadding='0'>
							<thead>
								<tr>
									<th>Team</th>
									<th>GP</th>
									<th>W</th>
									<th>L</th>
									<th>T</th>
									<th>OTL</th>
									<th>SOL</th>
									<th>GF</th>
									<th>GA</th>
									<th>Pts</th>
								</tr>
							</thead>
						</table>
					</div>
				)}
			</React.Fragment>
		</div>
	)
}

export default Standings
